import confetti from 'canvas-confetti';
import { Award, Gamepad2, Home, ListChecks, PlayCircle, Star } from 'lucide-react';
import { animate, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import type { CompletionPayload } from '../../../lib/types';
import { ButtonLink, Card, ReadAloud } from '../../../ui';

const BRAND = ['#5b47e0', '#f2a93b', '#16a57f', '#e56a4c', '#ffffff'];

function CountUp({ to }: { to: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const c = animate(0, to, { duration: 1.4, ease: 'easeOut', onUpdate: (x) => setV(Math.round(x)) });
    return () => c.stop();
  }, [to]);
  return <>{v}</>;
}

export function CelebrationStage({ result, nextLessonId }: { result: CompletionPayload; nextLessonId?: string }) {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const fire = (x: number, angle: number) =>
      confetti({ particleCount: 90, spread: 70, angle, origin: { x, y: 0.7 }, colors: BRAND, scalar: 1.1, disableForReducedMotion: true });
    fire(0.1, 60);
    fire(0.9, 120);
    const t = setTimeout(() => confetti({ particleCount: 140, spread: 110, origin: { y: 0.45 }, colors: BRAND, disableForReducedMotion: true }), 650);
    return () => clearTimeout(t);
  }, [reduced]);

  const rows = [
    { label: 'Video questions', icon: PlayCircle, ...result.breakdown.video },
    { label: 'Game', icon: Gamepad2, ...result.breakdown.game },
    { label: 'Quiz', icon: ListChecks, ...result.breakdown.quiz },
  ];

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6 text-center">
      <motion.div
        initial={{ scale: 0.3, rotate: -25, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 14 }}
        className="flex size-28 items-center justify-center rounded-[2rem] bg-gold shadow-[0_8px_0_0_var(--color-gold-strong)]"
      >
        <Star className="size-16 fill-white text-white" aria-hidden />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <h1 className="text-4xl font-extrabold sm:text-5xl">Lesson complete!</h1>
        <p className="mt-3 font-display text-2xl font-bold text-brand">
          You earned <CountUp to={result.stars} /> stars
        </p>
      </motion.div>

      <Card className="w-full p-5 text-left">
        <ul className="flex flex-col divide-y divide-line">
          {rows.map((r, i) => (
            <motion.li
              key={r.label}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.15 }}
              className="flex items-center gap-3 py-3"
            >
              <r.icon className="size-5 text-brand" aria-hidden />
              <span className="flex-1 font-semibold">{r.label}</span>
              <span className="inline-flex items-center gap-1 font-display font-bold">
                <Star className="size-4 fill-gold text-gold" aria-hidden />
                {r.stars} <span className="font-normal text-muted">/ {r.max}</span>
              </span>
            </motion.li>
          ))}
        </ul>
      </Card>

      {result.badge && (
        <motion.div
          initial={{ opacity: 0, rotateY: 90 }}
          animate={{ opacity: 1, rotateY: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
          className="flex w-full items-center gap-4 rounded-[var(--radius-card)] bg-ink p-5 text-left text-white"
        >
          <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-gold">
            <Award className="size-8 text-ink" aria-hidden />
          </span>
          <span>
            <span className="block text-sm font-semibold text-gold">{result.firstCompletion ? 'New badge' : 'Your badge'}</span>
            <span className="block font-display text-2xl font-extrabold">{result.badge.title}</span>
          </span>
        </motion.div>
      )}

      <Card className="w-full p-6 text-left">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-extrabold">What you learned</h2>
          <ReadAloud text={result.recap.join('. ')} />
        </div>
        <ul className="mt-3 flex flex-col gap-2">
          {result.recap.map((r) => (
            <li key={r} className="flex items-start gap-3 text-lg">
              <span className="mt-2.5 size-2 shrink-0 rounded-full bg-mint" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </Card>

      <div className="flex flex-wrap justify-center gap-3">
        {nextLessonId && (
          <ButtonLink to={`/learn/lesson/${nextLessonId}`} reloadDocument size="lg" variant="gold">
            Next lesson
          </ButtonLink>
        )}
        <ButtonLink to="/learn" size="lg" variant="secondary" icon={<Home className="size-5" aria-hidden />}>
          Back to my lessons
        </ButtonLink>
      </div>
    </div>
  );
}
